/**
 * Checkout as the customer sees it (ONB-040 to ONB-044, ONB-046): the formula, protein and flavor
 * choices, add-on prices, and a shipping address that has to be a deliverable US address in a
 * launch state. Payment itself is PAY-001 / lattice-4r0.
 *
 * Q-006 (launch states) and Q-019 (protein bases) are open. The lists here are engineering
 * defaults so checkout can validate and offer choices before those close.
 */
import type { OrderState } from "./machines.ts";
import type { Cents } from "./money.ts";
import { cents } from "./money.ts";
import { isLaunchState } from "./scripts.ts";

/** The monthly price, for one pouch a month. */
export const MONTHLY_CENTS: Cents = cents(175);

/**
 * Price for an add-on with no catalog price of its own. Omega-3 is the one add-on the provisional
 * catalog ships separately, at $5 a month.
 */
export const DEFAULT_ADDON_CENTS: Cents = cents(5);

export const FLAVORS = ["vanilla", "chocolate"] as const;

export type Flavor = (typeof FLAVORS)[number];

export function isFlavor(value: string): value is Flavor {
  return FLAVORS.some((flavor) => flavor === value);
}

/**
 * Every US state and DC, plus the inhabited territories. Territories are US addresses but not
 * launch states until Q-006 says otherwise.
 */
export const US_REGIONS = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "GA",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KS",
  "KY",
  "LA",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MO",
  "MS",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NM",
  "NV",
  "NY",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
  "AS",
  "GU",
  "MP",
  "PR",
  "VI",
] as const;

export type UsRegion = (typeof US_REGIONS)[number];

const ZIP = /^\d{5}(-\d{4})?$/;

export interface ShippingAddress {
  readonly line1: string;
  readonly line2: string;
  readonly city: string;
  readonly state: string;
  readonly zip: string;
}

export type AddressProblem = "incomplete" | "not_us" | "not_launch_state" | "bad_zip";

function isUsRegion(value: string): value is UsRegion {
  return US_REGIONS.some((region) => region === value);
}

function trimmed(value: string | undefined): string {
  return value?.trim() ?? "";
}

/**
 * The address fields a checkout form posts, trimmed. Missing keys become empty strings so the
 * problem function can say incomplete rather than the parser inventing a city.
 */
export function readAddress(
  body: {
    readonly line1?: string;
    readonly line2?: string;
    readonly city?: string;
    readonly state?: string;
    readonly zip?: string;
  } | null,
): ShippingAddress | null {
  if (!body) return null;

  return {
    line1: trimmed(body.line1),
    line2: trimmed(body.line2),
    city: trimmed(body.city),
    state: trimmed(body.state).toUpperCase(),
    zip: trimmed(body.zip).toUpperCase(),
  };
}

/** Why this address cannot ship, or null when it can. Launch states are Q-006. */
export function addressProblem(address: ShippingAddress): AddressProblem | null {
  if (address.line1 === "" || address.city === "" || address.state === "" || address.zip === "")
    return "incomplete";

  if (!ZIP.test(address.zip)) return "bad_zip";

  if (!isUsRegion(address.state)) return "not_us";

  if (!isLaunchState(address.state)) return "not_launch_state";

  return null;
}

export interface CheckoutIngredient {
  readonly ingredientId: string;
  readonly name: string;
  readonly dose: number;
  readonly unit: string;
  /** One plain sentence the customer can read (FOR-030, P-04). */
  readonly reason: string;
  readonly isProteinBase: boolean;
  readonly separateAddon: boolean;
  /** Set on add-ons that ship beside the pouch. Null for blended ingredients. */
  readonly priceCents: Cents | null;
}

export interface ProteinOption {
  readonly ingredientId: string;
  readonly name: string;
}

export interface RestorableIngredient {
  readonly ingredientId: string;
  readonly name: string;
}

export type CheckoutStatus = "draft" | "in_review" | "approved";

/**
 * Where the customer's first payment stands, read from their orders. `confirming` means Pay opened
 * a Stripe session and its webhook has not applied the payment yet. A customer sent back from
 * Stripe usually sees this for a few seconds, and keeps seeing it if they left Stripe without
 * paying.
 */
export type CheckoutPayment = "unpaid" | "confirming" | "confirmed";

/*
  A paid or released order counts as paid even after the subscription is cancelled. A refunded or
  cancelled first order opens checkout again.
*/
export function checkoutPaymentOf(orders: readonly OrderState[]): CheckoutPayment {
  if (orders.some((state) => state === "paid" || state === "released")) return "confirmed";

  return orders.includes("pending_payment") ? "confirming" : "unpaid";
}

/** Something left to do before this customer can pay, in the order checkout lists them. */
export type PayBlocker = "confirm_email" | "physician_signature";

/**
 * What stands between the customer and payment. Pay needs a verified email (the pay route's access)
 * and a formula a physician has signed (CLI-030). Checkout lists what is missing instead of offering
 * a button the Worker would refuse.
 */
export function payBlockers(status: CheckoutStatus, verified: boolean): readonly PayBlocker[] {
  const blockers: PayBlocker[] = [];

  if (!verified) blockers.push("confirm_email");

  if (status !== "approved") blockers.push("physician_signature");

  return blockers;
}

export interface CheckoutView {
  readonly formulaVersionId: string;
  readonly status: CheckoutStatus;
  readonly payment: CheckoutPayment;
  /** Empty when the customer can pay now. */
  readonly waitingOn: readonly PayBlocker[];
  readonly flavor: Flavor | null;
  readonly proteinBaseIngredientId: string | null;
  readonly ingredients: readonly CheckoutIngredient[];
  readonly proteinOptions: readonly ProteinOption[];
  readonly restorable: readonly RestorableIngredient[];
  readonly monthlyCents: Cents;
  readonly addonsCents: Cents;
  readonly totalCents: Cents;
}

export function addonPriceCents(stored: number | null, separateAddon: boolean): Cents | null {
  if (!separateAddon) return null;

  return stored ?? DEFAULT_ADDON_CENTS;
}

export function addonsTotal(ingredients: readonly CheckoutIngredient[]): Cents {
  return ingredients.reduce((sum, item) => sum + (item.priceCents ?? 0), 0);
}
